// React Dependencies
import React, { useEffect, useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';
import { DateRange } from '@material-ui/icons';
import { TextFieldStyle } from '../../MTextField';

const useStyles = makeStyles({
  root: {
    '& .MuiInputBase-root': {
      padding: '0 !important',
      margin:  '0 !important',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10.5px 14px !important',
    },
    '& .MuiOutlinedInput-adornedEnd': {
      paddingLeft:  0,
      paddingRight: 0,
      margin:       0,
    },
    '& .MuiIconButton-root': {
      padding: 5,
    },
  },
});

const MaterialDatePicker = ({
  disablePast,
  label,
  value,
  onChange,
  minDate,
  maxDate,
  style,
  readOnly,
  variant,
  format,
  disabled,
  className,
  size,
  fontSize,
  clearable,
}) => {
  const classes = useStyles();

  const [selectedDate, setSelectedDate] = useState(null);

    useEffect(() => {
      setSelectedDate(value || null);
    }, [value]);

    const handleDateChange = (date) => {
      const momentDate = moment(date);

      if (momentDate.isValid()) {
        setSelectedDate(date);
        onChange(date);
      } else if (date === null) {
        setSelectedDate(null);
        onChange(null);
      } else {
        const fallbackDate = minDate || new Date();

        setSelectedDate(fallbackDate);
        onChange(fallbackDate);
      }
    };

    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <KeyboardDatePicker
          className={`${classes.root} ${className}`}
          keyboardIcon={<DateRange style={{ width: '1.3rem', height: '1rem', objectFit: 'contain' }} />}
          inputProps={{
            style: {
              fontSize,
            },
          }}
          disableToolbar
          autoOk
          inputVariant={variant}
          format={format}
          margin="normal"
          label={label}
          value={selectedDate}
          onChange={handleDateChange}
          readOnly={readOnly}
          KeyboardButtonProps={{
            'aria-label': 'Select date',
          }}
          minDate={minDate}
          maxDate={maxDate}
          onKeyDown={(e) => e.preventDefault()}
          style={style}
          disabled={disabled}
          size={size}
          disablePast={disablePast}
          clearable={clearable}
          clearLabel="Clear"
        />
      </MuiPickersUtilsProvider>
    );
};

MaterialDatePicker.propTypes = {
  label:       PropTypes.string,
  value:       PropTypes.shape(Object),
  onChange:    PropTypes.func.isRequired,
  format:      PropTypes.string,
  variant:     PropTypes.string,
  disabled:    PropTypes.bool,
  disablePast: PropTypes.bool,
  readOnly:    PropTypes.bool,
  minDate:     PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.bool]),
  maxDate:     PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.bool]),
  style:       PropTypes.shape(Object),
  className:   PropTypes.string,
  clearable:   PropTypes.bool,
  size:        PropTypes.oneOf(['small', 'medium', 'large']),
  fontSize:    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

MaterialDatePicker.defaultProps = {
  label:       '',
  value:       null,
  format:      'dd/MM/yyyy',
  variant:     'inline',
  readOnly:    false,
  disabled:    false,
  disablePast: false,
  minDate:     false,
  maxDate:     undefined,
  style:       {},
  className:   '',
  size:        'medium',
  fontSize:    TextFieldStyle.text.size,
  clearable:   true,
};

export default MaterialDatePicker;
